/**
 * botHelpers.js — Shared utilities for bot decision-making algorithms.
 *
 * Pure functions: no React, no Supabase, no side effects.
 * Used by bot implementations in botUtils.js.
 */

import { calculateScore, placeMarker, getNextActivePlayer } from './gameLogic.js';
import { getColorIslands, getIslandRootLookup, ORTHO_DIRECTIONS, toCellKey } from './islandTraversal.js';

// ---------------------------------------------------------------------------
// Board analysis
// ---------------------------------------------------------------------------

/**
 * Get all available (playable) positions on the board.
 * @param {Array} gameBoard
 * @returns {Array<{row: number, col: number}>}
 */
export function getAvailablePositions(gameBoard) {
  const positions = [];
  for (let row = 0; row < gameBoard.length; row++) {
    for (let col = 0; col < gameBoard[0].length; col++) {
      if (gameBoard[row][col] === 'Y') {
        positions.push({ row, col });
      }
    }
  }
  return positions;
}

/**
 * Get the 4-directional neighbor cell values for a position.
 * @returns {Array} — values of neighboring cells (colorIndex number, 'Y', 'X', or 'B')
 */
export function getNeighborValues(gameBoard, row, col) {
  const values = [];
  for (const [dy, dx] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
    const nr = row + dy, nc = col + dx;
    if (nr >= 0 && nr < gameBoard.length && nc >= 0 && nc < gameBoard[0].length) {
      values.push(gameBoard[nr][nc]);
    }
  }
  return values;
}

// ---------------------------------------------------------------------------
// Island analysis
// ---------------------------------------------------------------------------

/**
 * Get sizes of all islands for a given color on the board.
 * Uses BFS. Returns array of sizes, e.g. [3, 1, 5].
 *
 * @param {Array} gameBoard
 * @param {number} colorIndex
 * @returns {number[]}
 */
export function getIslandSizes(gameBoard, colorIndex) {
  return getColorIslands(gameBoard, colorIndex).map((island) => island.size);
}

/**
 * Count empty ('Y') cells adjacent to any cell of the given color.
 * This represents expansion potential — how many places the player could grow.
 *
 * @param {Array} gameBoard
 * @param {number} colorIndex
 * @returns {number}
 */
export function countExpandableNeighbors(gameBoard, colorIndex) {
  const rows = gameBoard.length;
  const cols = gameBoard[0].length;
  const counted = new Set();
  let count = 0;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (gameBoard[r][c] === colorIndex) {
        for (const [dy, dx] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
          const nr = r + dy, nc = c + dx;
          if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
            const key = nr * cols + nc;
            if (!counted.has(key) && gameBoard[nr][nc] === 'Y') {
              counted.add(key);
              count++;
            }
          }
        }
      }
    }
  }
  return count;
}

// ---------------------------------------------------------------------------
// Position expansion potential
// ---------------------------------------------------------------------------

/**
 * Count how many adjacent cells of a position are empty ('Y') or wall ('X').
 * Y means directly playable, X means it could open up if an adjacent marker is placed.
 * This measures how many directions the island can grow from this position.
 *
 * @param {Array} gameBoard
 * @param {number} row
 * @param {number} col
 * @returns {{ emptyNeighbors: number, wallNeighbors: number }}
 */
export function positionExpansionPotential(gameBoard, row, col) {
  const rows = gameBoard.length;
  const cols = gameBoard[0].length;
  let emptyNeighbors = 0;
  let wallNeighbors = 0;

  for (const [dy, dx] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
    const nr = row + dy, nc = col + dx;
    if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
      if (gameBoard[nr][nc] === 'Y') emptyNeighbors++;
      else if (gameBoard[nr][nc] === 'X') wallNeighbors++;
    }
  }
  return { emptyNeighbors, wallNeighbors };
}

/**
 * After simulating a placement at (row, col) for colorIndex, count how many
 * unique empty ('Y') cells border the resulting island. This is the island's
 * total expansion frontier — higher = more future growth options.
 *
 * @param {Array} gameBoard — board BEFORE placement
 * @param {number} row
 * @param {number} col
 * @param {number} colorIndex
 * @returns {number}
 */
export function expansionFrontierAfterPlace(gameBoard, row, col, colorIndex) {
  const cols = gameBoard[0].length;
  // Simulate the real move, including X -> Y reveals around the placed marker.
  const board = placeMarker(gameBoard, row, col, colorIndex);
  const islands = getColorIslands(board, colorIndex);
  const lookup = getIslandRootLookup(islands, cols);
  const targetRoot = lookup.get(toCellKey(row, col, cols));
  if (targetRoot === undefined) {
    return 0;
  }

  const targetIsland = islands.find((island) => island.rootKey === targetRoot);
  if (!targetIsland) {
    return 0;
  }

  const frontier = new Set();
  targetIsland.cells.forEach(([cellRow, cellCol]) => {
    for (const [dr, dc] of ORTHO_DIRECTIONS) {
      const nr = cellRow + dr;
      const nc = cellCol + dc;
      if (nr >= 0 && nr < board.length && nc >= 0 && nc < cols && board[nr][nc] === 'Y') {
        frontier.add(toCellKey(nr, nc, cols));
      }
    }
  });

  return frontier.size;
}

/**
 * Count how many numeric markers are already on the board.
 * Useful for opening heuristics.
 *
 * @param {Array} gameBoard
 * @returns {number}
 */
export function countPlacedMarkers(gameBoard) {
  let count = 0;
  for (let r = 0; r < gameBoard.length; r++) {
    for (let c = 0; c < gameBoard[0].length; c++) {
      if (typeof gameBoard[r][c] === 'number') count++;
    }
  }
  return count;
}

/**
 * Count the current trailing streak of swap moves.
 *
 * @param {Array} moves
 * @returns {number}
 */
export function getConsecutiveSwapCount(moves = []) {
  let count = 0;
  for (let i = moves.length - 1; i >= 0; i--) {
    if (moves[i]?.type !== 'swap') break;
    count++;
  }
  return count;
}

/**
 * Count how many swaps a given player has already used in the current game.
 *
 * @param {Array} moves
 * @param {number} playerIndex
 * @returns {number}
 */
export function getPlayerSwapCount(moves = [], playerIndex = null) {
  if (playerIndex === null || playerIndex === undefined) return 0;
  return moves.reduce((count, move) => (
    move?.type === 'swap' && move.player === playerIndex ? count + 1 : count
  ), 0);
}

/**
 * Return how many swaps a player still has left under the active room rule.
 * Returns null when the match has no per-player swap cap.
 *
 * @param {Array} moves
 * @param {number} playerIndex
 * @param {number|null} swapLimitPerPlayer
 * @returns {number|null}
 */
export function getRemainingSwapsForPlayer(moves = [], playerIndex = null, swapLimitPerPlayer = null) {
  const effectiveSwapLimit = typeof swapLimitPerPlayer === 'number' && swapLimitPerPlayer > 0
    ? swapLimitPerPlayer
    : null;
  if (effectiveSwapLimit === null) return null;
  return Math.max(0, effectiveSwapLimit - getPlayerSwapCount(moves, playerIndex));
}

/**
 * Whether the current turn is allowed to swap.
 * Swap is locked once the trailing swap streak reaches the room size.
 *
 * @param {Object} params
 * @param {Array} params.players
 * @param {Array} params.markersPool
 * @param {Array} params.moveHistory
 * @param {number} [params.consecutiveSwaps]
 * @param {number} [params.currentPlayer]
 * @param {number|null} [params.swapLimitPerPlayer]
 * @param {number|null} [params.playerSwapCount]
 * @returns {boolean}
 */
export function canCurrentPlayerSwap({
  players = [],
  markersPool = [],
  moveHistory = [],
  consecutiveSwaps = null,
  currentPlayer = 0,
  swapLimitPerPlayer = null,
  playerSwapCount = null,
}) {
  if (markersPool.length === 0) return false;
  if (players.length === 0) return false;
  const streak = consecutiveSwaps ?? getConsecutiveSwapCount(moveHistory);
  if (streak >= players.length) return false;

  const effectiveSwapLimit = typeof swapLimitPerPlayer === 'number' && swapLimitPerPlayer > 0
    ? swapLimitPerPlayer
    : null;

  if (effectiveSwapLimit === null) {
    return true;
  }

  const usedSwaps = playerSwapCount ?? getPlayerSwapCount(moveHistory, currentPlayer);
  return usedSwaps < effectiveSwapLimit;
}

/**
 * Return the size of the largest island for a given color.
 *
 * @param {Array} gameBoard
 * @param {number} colorIndex
 * @returns {number} — 0 if no markers of that color exist
 */
export function largestIslandSize(gameBoard, colorIndex) {
  const sizes = getIslandSizes(gameBoard, colorIndex);
  return sizes.length > 0 ? Math.max(...sizes) : 0;
}

/**
 * Count how many distinct same-color islands neighbor  position (row, col).
 * If this count is ≥ 2, placing here bridges (merges) multiple islands — a
 * potentially huge score jump.
 *
 * @param {Array} gameBoard
 * @param {number} row
 * @param {number} col
 * @param {number} colorIndex
 * @returns {number} — number of distinct islands that would merge (0, 1, 2, 3, or 4)
 */
export function countIslandsTouching(gameBoard, row, col, colorIndex) {
  const cols = gameBoard[0].length;
  const islands = getColorIslands(gameBoard, colorIndex);
  const islandRootsByCell = getIslandRootLookup(islands, cols);
  const islandRoots = new Set();

  for (const [dy, dx] of ORTHO_DIRECTIONS) {
    const nr = row + dy, nc = col + dx;
    if (nr >= 0 && nr < gameBoard.length && nc >= 0 && nc < cols && gameBoard[nr][nc] === colorIndex) {
      const root = islandRootsByCell.get(toCellKey(nr, nc, cols));
      if (root !== undefined) {
        islandRoots.add(root);
      }
    }
  }
  return islandRoots.size;
}

/**
 * Get score ranking of all players (sorted descending).
 * Returns array of { playerIndex, score }.
 *
 * @param {Array} gameBoard
 * @param {Array} players — player objects with colorIndex
 * @returns {Array<{playerIndex: number, score: number}>}
 */
export function getScoreRanking(gameBoard, players) {
  return players
    .map((p, i) => ({ playerIndex: i, score: calculateScore(gameBoard, p.colorIndex) }))
    .sort((a, b) => b.score - a.score);
}

// ---------------------------------------------------------------------------
// Score helpers
// ---------------------------------------------------------------------------

/**
 * Compute the score delta for placing a marker of `colorIndex` at (row, col).
 * Uses the real calculateScore from gameLogic.js for correctness.
 *
 * @param {Array} gameBoard
 * @param {number} row
 * @param {number} col
 * @param {number} colorIndex
 * @returns {number} — score change (can be 0)
 */
export function deltaScore(gameBoard, row, col, colorIndex) {
  const oldScore = calculateScore(gameBoard, colorIndex);
  const newBoard = placeMarker(gameBoard, row, col, colorIndex);
  const newScore = calculateScore(newBoard, colorIndex);
  return newScore - oldScore;
}

// ---------------------------------------------------------------------------
// Game state simulation helpers
// ---------------------------------------------------------------------------

/**
 * Lightweight deep clone of game state for simulations.
 * Avoids JSON.parse(JSON.stringify(...)) overhead.
 */
export function cloneState(gameState) {
  return {
    gameBoard: gameState.gameBoard.map(row => [...row]),
    players: gameState.players.map(p => ({
      ...p,
      markers: p.markers.map(m => ({ ...m }))
    })),
    markersPool: gameState.markersPool.map(m => ({ ...m })),
    currentPlayer: gameState.currentPlayer,
    moveHistory: [...(gameState.moveHistory ?? [])],
    settings: { ...(gameState.settings ?? {}) },
    consecutiveSwaps: gameState.consecutiveSwaps ?? 0,
  };
}

/**
 * Apply a move to a game state. Returns a new state (does not mutate input).
 *
 * @param {Object} gameState
 * @param {Object} move — { action: 'place', markerIndex, position } or { action: 'swap', targetPlayerId }
 * @param {number} playerIndex — index of the acting player
 * @returns {Object} — new game state after the move
 */
export function applyMoveToState(gameState, move, playerIndex) {
  const newPlayers = gameState.players.map(p => ({
    ...p,
    markers: [...p.markers]
  }));
  let newBoard = gameState.gameBoard;
  const newPool = [...gameState.markersPool];
  const moveHistory = [...(gameState.moveHistory ?? [])];
  let consecutiveSwaps = gameState.consecutiveSwaps ?? 0;

  if (move.action === 'place' && move.position) {
    const marker = newPlayers[playerIndex].markers[move.markerIndex];
    if (marker) {
      newBoard = placeMarker(gameState.gameBoard, move.position.row, move.position.col, marker.colorIndex);
      newPlayers[playerIndex].markers.splice(move.markerIndex, 1);
      if (newPool.length > 0) {
        newPlayers[playerIndex].markers.push(newPool.shift());
      }
      moveHistory.push({
        type: 'place',
        player: playerIndex,
        row: move.position.row,
        col: move.position.col,
        colorIndex: marker.colorIndex,
      });
      consecutiveSwaps = 0;
    }
  } else if (move.action === 'swap') {
    const myMarkers = newPlayers[playerIndex].markers;
    newPlayers[playerIndex].markers = [...newPlayers[move.targetPlayerId].markers];
    newPlayers[move.targetPlayerId].markers = myMarkers;
    moveHistory.push({ type: 'swap', player: playerIndex, target: move.targetPlayerId });
    consecutiveSwaps += 1;
  }

  // Advance to next active player (skip players with no markers)
  const nextPlayer = getNextActivePlayer(playerIndex, newPlayers) ?? (playerIndex + 1) % newPlayers.length;

  return {
    gameBoard: newBoard,
    players: newPlayers,
    markersPool: newPool,
    currentPlayer: nextPlayer,
    moveHistory,
    settings: { ...(gameState.settings ?? {}) },
    consecutiveSwaps,
  };
}

/**
 * Check if the game is over (no markers left anywhere).
 */
export function isGameOver(gameState) {
  return gameState.players.every(p => p.markers.length === 0) &&
    gameState.markersPool.length === 0;
}

// ---------------------------------------------------------------------------
// Game phase detection
// ---------------------------------------------------------------------------

/**
 * Detect game phase based on how many markers remain.
 * @returns {'opening' | 'midgame' | 'endgame'}
 */
export function detectGamePhase(gameState) {
  const markersInHands = gameState.players.reduce((sum, p) => sum + p.markers.length, 0);
  const totalRemaining = markersInHands + gameState.markersPool.length;
  const maxTotal = gameState.players.length * 20;
  const remainingFraction = totalRemaining / maxTotal;

  if (remainingFraction > 0.75) return 'opening';
  if (remainingFraction > 0.35) return 'midgame';
  return 'endgame';
}
